import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useGlobal } from '@/context/GlobalContext';
import { usePageContent, getContent } from '@/hooks/usePageContent';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import SEO, { organizationSchema } from '@/components/SEO';
import { 
  ArrowRight, Lightbulb, Cog, Factory, Truck, HeadphonesIcon,
  Camera, Radio, ShieldCheck, Globe, ChevronRight,
  Send, ExternalLink
} from 'lucide-react';

const HomePage = () => {
  const { settings, API } = useGlobal();
  const { content, loading: contentLoading } = usePageContent('home');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    company: '',
    website: '',
    email: '',
    project_type: '',
    message: ''
  });

  // Get content with fallbacks
  const hero = content.hero || {};
  const pillars = content.pillars || {};
  const process = content.process || {};
  const usecases = content.usecases || {};
  const whyUs = content.why_us || {};
  const anchilex = content.anchilex || {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        project_type: formData.project_type,
        message: formData.message
      });
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', phone: '', company: '', website: '', email: '', project_type: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Default values (used if CMS content not available)
  const defaults = {
    hero: {
      badge: "IMAGING & SAFETY • OEM / ODM",
      headline: "Clear Vision for Every Journey & Every Space",
      description: "Anchilex (Trinity Electronics) delivers vehicle imaging, surveillance, and ultrasonic deterrence — ready portfolios and tailored OEM/ODM support backed by 25+ years of expertise.",
      cta_primary: "Explore Solutions",
      cta_secondary: "Start Your Project",
      badges: ["25+ Years Expertise", "Global OEM/ODM", "Automotive-Grade Chipsets"],
      background_image: "https://static.prod-images.emergentagent.com/jobs/a9e4d36a-e5ff-43e1-ab5f-86fa29a8738a/images/f7af2744eda1a28c37533f7f310272cd93ab431ecef070ccdc2db85bc39554d0.png"
    },
    pillars: {
      title: "Three Ways We Help You Succeed",
      description: "Whether you need custom development, ready-to-brand products, or expert consulting — we've got you covered.",
      pillar1: {
        title: "Imaging Solutions",
        description: "Dashcams, e-mirrors, MDVRs, and surveillance systems. We work with automotive-grade chipsets to deliver reliable, high-performance imaging products.",
        features: ["Automotive-grade SoC expertise", "OEM/ODM & private label", "ISP tuning & IQ optimization"]
      },
      pillar2: {
        title: "Ultrasonic Deterrence",
        description: "Humane bird and animal deterrence for airports, heritage sites, warehouses, and power facilities. We consult, configure, and supply.",
        features: ["Site assessment & consulting", "Custom configuration", "Global distribution partner"]
      },
      pillar3: {
        title: "Engineering Services",
        description: "From concept to certified product. We handle design, prototyping, and manufacturing coordination — so you can focus on your market.",
        features: ["Product design & development", "Manufacturing coordination", "Certification support (CE, FCC, RoHS)"]
      }
    },
    process: {
      title: "How We Work",
      description: "From initial concept to delivered product — a proven process refined over decades.",
      steps: [
        { title: "Consult", description: "Understand your requirements and market goals" },
        { title: "Design", description: "Engineer a solution tailored to your specs" },
        { title: "Source", description: "Select optimal manufacturing partners" },
        { title: "Deliver", description: "Quality assurance and logistics" },
        { title: "Support", description: "Ongoing technical assistance" }
      ]
    },
    usecases: {
      title: "Typical Applications",
      description: "Real-world scenarios where our solutions make an impact.",
      cases: [
        {
          category: "FLEET MANAGEMENT",
          title: "Multi-Channel Vehicle Recording",
          description: "A logistics operator needs real-time monitoring across 200+ vehicles. Our 8-channel MDVR with 4G connectivity and GPS provides fleet-wide visibility and incident documentation.",
          tags: ["MDVR", "4G/GPS", "Fleet"],
          image: "https://images.unsplash.com/photo-1755728531186-4efe610d8502?w=600&q=80"
        },
        {
          category: "WILDLIFE MANAGEMENT",
          title: "Airport Bird Strike Prevention",
          description: "An international airport requires a humane, non-lethal solution to reduce bird strikes. Our ultrasonic system is configured for runway coverage patterns and migratory seasons.",
          tags: ["Ultrasonic", "Aviation", "Consulting"],
          image: "https://images.unsplash.com/photo-1545460463-afdcfb3a3e53?w=600&q=80"
        },
        {
          category: "PRIVATE LABEL",
          title: "Brand-Ready Dashcam Line",
          description: "An automotive accessories brand wants to expand into dashcams without R&D investment. We provide a ready product line for their branding with packaging and certification support.",
          tags: ["OEM/ODM", "Dashcam", "White Label"],
          image: "https://images.unsplash.com/photo-1694582862276-0afe82801621?w=600&q=80"
        }
      ]
    },
    why_us: {
      title: "Why Partner With Trinity?",
      description: "With 30+ years in imaging technology, we bring deep engineering expertise and a network of certified manufacturing partners. We design the solution, manage the complexity, and deliver quality you can trace.",
      points: [
        { title: "Engineering-First Approach", description: "We start with your specs, not a production line. Our engineers design the optimal solution, then source the right manufacturing partner." },
        { title: "Aligned Incentives", description: "We succeed when you succeed. Our role is to deliver the right solution for your market — not to fill capacity quotas." },
        { title: "Global Reach, Local Support", description: "Engineering support in English, Mandarin, German, French, Russian, Swedish, and Japanese." }
      ],
      stats: [
        { value: "30+", label: "Years in Imaging", sublabel: "Optics • ISP • IQ Tuning" },
        { value: "25+", label: "Countries Served", sublabel: "Local compliance expertise" },
        { value: "100+", label: "OEM/ODM Projects", sublabel: "Design to delivery" },
        { value: "7", label: "Languages", sublabel: "Global support team" }
      ]
    },
    anchilex: {
      label: "OUR CONSUMER BRAND",
      title: "Looking for Motorcycle Dashcams?",
      description: "Visit Anchilex.com for our complete lineup of motorcycle recording solutions — available for purchase or distribution partnership.",
      cta: "Visit Anchilex.com",
      url: "https://www.anchilex.com"
    }
  };

  // Merge CMS content with defaults
  const h = { ...defaults.hero, ...hero };
  const p = { ...defaults.pillars, ...pillars };
  const pr = { ...defaults.process, ...process };
  const u = { ...defaults.usecases, ...usecases };
  const w = { ...defaults.why_us, ...whyUs };
  const a = { ...defaults.anchilex, ...anchilex };

  const stepIcons = [Lightbulb, Cog, Factory, Truck, HeadphonesIcon];

  return (
    <div className="min-h-screen bg-[#1a1f2e]" data-testid="home-page">
      <SEO 
        title="OEM/ODM Imaging & Safety Solutions"
        description={h.description}
        keywords="dashcam manufacturer, vehicle camera OEM, MDVR supplier, surveillance camera ODM, ultrasonic bird deterrent, automotive imaging Taiwan, private label dashcam, fleet camera system, Sony sensor, Sony STARVIS, automotive vehicle camera, CCTV IPC, wholesale camera supplier, camera distributor"
        canonicalUrl="https://www.trinity-tw.com"
        structuredData={organizationSchema}
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-24 px-4 sm:px-6 lg:px-8 relative" data-testid="hero-section">
        {/* Background Image */}
        <div 
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage: `url(${h.background_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1f2e]/50 via-[#1a1f2e]/70 to-[#1a1f2e]/80" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#2a324a] border border-[#3d4758] text-sm text-slate-300 mb-6">
              {h.badge}
            </div>
            
            {/* Headline */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 font-['Montserrat'] leading-[1.1]">
              {h.headline}
            </h1>
            
            {/* Description */}
            <p className="text-lg text-slate-400 mb-8 leading-relaxed max-w-2xl">
              {h.description}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-6">
              <Link to="/mobility-imaging">
                <Button className="bg-[#5b3589] hover:bg-[#6d3d9e] text-white rounded-full px-8 py-6 text-base font-medium">
                  {h.cta_primary}
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="bg-[#2a324a] hover:bg-[#3d4758] text-white border border-[#3d4758] rounded-full px-8 py-6 text-base font-medium">
                  {h.cta_secondary}
                </Button>
              </Link>
            </div>

            {/* Small Badges */}
            <div className="flex flex-wrap gap-3">
              {(h.badges || []).map((badge, idx) => (
                <span key={idx} className="px-4 py-2 rounded-full bg-transparent border border-[#3d4758] text-sm text-slate-400">
                  {badge}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What We Do - Three Pillars */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Montserrat']">
              {p.title}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {p.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Pillar 1: Imaging */}
            <div className="rounded-2xl bg-[#232a3b] border border-[#3d4758] p-8 hover:border-[#5b3589] transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#5b3589]/20 flex items-center justify-center mb-6">
                <Camera className="w-6 h-6 text-[#8b5cf6]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 font-['Montserrat']">
                {p.pillar1?.title || defaults.pillars.pillar1.title}
              </h3>
              <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                {p.pillar1?.description || defaults.pillars.pillar1.description}
              </p>
              <div className="space-y-2 mb-6">
                {(p.pillar1?.features || defaults.pillars.pillar1.features).map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-slate-300 text-sm">
                    <ChevronRight className="w-4 h-4 text-[#5b3589]" />
                    {feature}
                  </div>
                ))}
              </div>
              <Link to="/mobility-imaging" className="inline-flex items-center text-[#8b5cf6] text-sm font-medium hover:underline">
                Learn more <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>

            {/* Pillar 2: Ultrasonic */}
            <div className="rounded-2xl bg-[#232a3b] border border-[#3d4758] p-8 hover:border-[#5b3589] transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#5b3589]/20 flex items-center justify-center mb-6">
                <Radio className="w-6 h-6 text-[#8b5cf6]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 font-['Montserrat']">
                {p.pillar2?.title || defaults.pillars.pillar2.title}
              </h3>
              <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                {p.pillar2?.description || defaults.pillars.pillar2.description}
              </p>
              <div className="space-y-2 mb-6">
                {(p.pillar2?.features || defaults.pillars.pillar2.features).map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-slate-300 text-sm">
                    <ChevronRight className="w-4 h-4 text-[#5b3589]" />
                    {feature}
                  </div>
                ))}
              </div>
              <Link to="/ultrasonic" className="inline-flex items-center text-[#8b5cf6] text-sm font-medium hover:underline">
                Learn more <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>

            {/* Pillar 3: Engineering */}
            <div className="rounded-2xl bg-[#232a3b] border border-[#3d4758] p-8 hover:border-[#5b3589] transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#5b3589]/20 flex items-center justify-center mb-6">
                <Cog className="w-6 h-6 text-[#8b5cf6]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 font-['Montserrat']">
                {p.pillar3?.title || defaults.pillars.pillar3.title}
              </h3>
              <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                {p.pillar3?.description || defaults.pillars.pillar3.description}
              </p>
              <div className="space-y-2 mb-6">
                {(p.pillar3?.features || defaults.pillars.pillar3.features).map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-slate-300 text-sm">
                    <ChevronRight className="w-4 h-4 text-[#5b3589]" />
                    {feature}
                  </div>
                ))}
              </div>
              <Link to="/engineering" className="inline-flex items-center text-[#8b5cf6] text-sm font-medium hover:underline">
                Learn more <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work - Process Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="process-section">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Montserrat']">
              {pr.title}
            </h2>
            <p className="text-slate-400 max-w-2xl">
              {pr.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {(pr.steps || defaults.process.steps).map((step, index) => {
              const StepIcon = stepIcons[index] || Cog;
              return (
                <div key={index} className="relative">
                  <div className="rounded-xl bg-[#232a3b] border border-[#3d4758] p-6 h-full">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 rounded-full bg-[#5b3589] flex items-center justify-center text-white text-sm font-bold">
                        {index + 1}
                      </div>
                      <StepIcon className="w-5 h-5 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2 font-['Montserrat']">{step.title}</h3>
                    <p className="text-slate-400 text-sm">{step.description}</p>
                  </div>
                  {index < 4 && (
                    <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                      <ChevronRight className="w-4 h-4 text-[#3d4758]" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]" data-testid="usecases-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Montserrat']">
              {u.title}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {u.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(u.cases || defaults.usecases.cases).map((useCase, idx) => (
              <div key={idx} className="rounded-2xl bg-[#232a3b] border border-[#3d4758] overflow-hidden">
                <div className="h-40 relative overflow-hidden">
                  <img 
                    src={useCase.image} 
                    alt={useCase.title}
                    className="w-full h-full object-cover opacity-60"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#232a3b] to-transparent" />
                </div>
                <div className="p-6">
                  <div className="text-xs text-[#8b5cf6] font-medium mb-2">{useCase.category}</div>
                  <h3 className="text-lg font-bold text-white mb-2 font-['Montserrat']">
                    {useCase.title}
                  </h3>
                  <p className="text-slate-400 text-sm mb-4">
                    {useCase.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(useCase.tags || []).map((tag, tidx) => (
                      <span key={tidx} className="px-2 py-1 rounded bg-[#2a324a] text-xs text-slate-400">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner With Us */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-['Montserrat']">
                {w.title}
              </h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                {w.description}
              </p>
              
              <div className="space-y-6">
                {(w.points || defaults.why_us.points).map((point, idx) => {
                  const icons = [Cog, ShieldCheck, Globe];
                  const Icon = icons[idx] || Cog;
                  return (
                    <div key={idx} className="flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#5b3589]/20 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#8b5cf6]" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold mb-1">{point.title}</h4>
                        <p className="text-slate-400 text-sm">{point.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              {(w.stats || defaults.why_us.stats).map((stat, idx) => (
                <div key={idx} className="rounded-xl bg-[#232a3b] border border-[#3d4758] p-6">
                  <div className="text-4xl font-bold text-white mb-1 font-['Montserrat']">
                    {stat.value}
                  </div>
                  <div className="text-slate-300 text-sm font-medium">{stat.label}</div>
                  <div className="text-slate-500 text-xs mt-1">{stat.sublabel}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Anchilex Brand Callout */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#5b3589]/20 to-[#1a1f2e]" data-testid="brand-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="text-xs text-[#8b5cf6] font-medium mb-2">{a.label}</div>
              <h3 className="text-2xl font-bold text-white mb-2 font-['Montserrat']">
                {a.title}
              </h3>
              <p className="text-slate-400">
                {a.description}
              </p>
            </div>
            <a 
              href={a.url || "https://www.anchilex.com"} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[#1a1f2e] px-6 py-3 rounded-full font-medium hover:bg-slate-100 transition-colors"
            >
              {a.cta}
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="contact-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Montserrat']">
            Let's Talk
          </h2>
          <p className="text-slate-400 mb-10">
            Whether you have a specific project in mind or just want to explore possibilities — we're here to help. Tell us about your needs and we'll get back to you with the best next steps.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-slate-300 text-sm mb-2">Your Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="bg-[#232a3b] border-[#3d4758] text-white placeholder-slate-500 rounded-lg"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="block text-slate-300 text-sm mb-2">Phone (include country code)</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="bg-[#232a3b] border-[#3d4758] text-white placeholder-slate-500 rounded-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-slate-300 text-sm mb-2">Company</label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                  className="bg-[#232a3b] border-[#3d4758] text-white placeholder-slate-500 rounded-lg"
                  data-testid="contact-company"
                />
              </div>
              <div>
                <label className="block text-slate-300 text-sm mb-2">Website</label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  className="bg-[#232a3b] border-[#3d4758] text-white placeholder-slate-500 rounded-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-slate-300 text-sm mb-2">Email *</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="bg-[#232a3b] border-[#3d4758] text-white placeholder-slate-500 rounded-lg"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="block text-slate-300 text-sm mb-2">I'm interested in... *</label>
                <Select 
                  value={formData.project_type} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, project_type: value }))}
                >
                  <SelectTrigger className="bg-[#232a3b] border-[#3d4758] text-white rounded-lg" data-testid="contact-project-type">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#232a3b] border-[#3d4758]">
                    <SelectItem value="OEM/ODM Development" className="text-slate-300">OEM/ODM Development</SelectItem>
                    <SelectItem value="Private Label Products" className="text-slate-300">Private Label Products</SelectItem>
                    <SelectItem value="Distribution Partnership" className="text-slate-300">Distribution Partnership</SelectItem>
                    <SelectItem value="Ultrasonic Consulting" className="text-slate-300">Ultrasonic Consulting</SelectItem>
                    <SelectItem value="General Inquiry" className="text-slate-300">General Inquiry</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-slate-300 text-sm mb-2">Tell us about your project or needs</label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                rows={4}
                placeholder="What are you looking to achieve? Any specific requirements, volumes, or timelines?"
                className="bg-[#232a3b] border-[#3d4758] text-white placeholder-slate-500 rounded-lg resize-none"
                data-testid="contact-message"
              />
            </div>

            <p className="text-slate-500 text-sm">
              We can respond in English, Mandarin, German, French, Russian, Swedish, or Japanese.
            </p>

            <Button 
              type="submit" 
              disabled={loading || !formData.name || !formData.email || !formData.project_type}
              className="bg-[#5b3589] hover:bg-[#6d3d9e] text-white rounded-full px-8 py-6 text-base font-medium"
              data-testid="contact-submit"
            >
              {loading ? 'Sending...' : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
